import type { SocialLinks, SocialPlatform } from "@/constants/social-icons";
import type { MetadataDTO } from "./metadata";

export interface YearItem {
    key: string;
    value: number;
}

export type ArtworkField = "medium" | "genre" | "style" | "";

export type ArtType =
    | "painting"
    | "sculpture"
    | "drawing"
    | "printmaking"
    | "handmade"
    | "mosaic"
    | "multimedia"
    | "book"
    | "";

export type ArtworkVenueType =
    | "museum"
    | "gallery"
    | "arts_centre"
    | "private"
    | "other";

export interface ArtTypeItem {
    id: number;
    key: ArtType;
    label: ILabel;
    is3D: boolean;
    icon: string;
}

export interface ILabel {
    hy: string;
    en: string;
    ru: string;
}

export interface ArtistPopulated {
    _id: string;
    name: string;
    email?: string;
    last_name: string;
    avatar: string;
    phone_number: string;
    social_links: SocialLinks;
}

// Define the new Color Object structure
export interface ColorPalette {
    r: number;
    g: number;
    b: number;
}

export interface ArtworkDimensions {
    height: number;
    width: number;
    unit: string;
    depth: number;
}

export interface ArtworkLocation {
    countryCode: string;
    city: ILabel;
    venue: {
        title: string;
        type: ArtworkVenueType;
    };
}

export interface Artwork {
    _id: string;
    userId: ArtistPopulated;
    title: ILabel;
    description: ILabel;
    artType: ArtType;
    dimensions: ArtworkDimensions;
    creationYear: number | null;
    provenance: string[];
    exhibitions: string[];
    currentLocation: string;
    mediums: MetadataDTO[];
    genres: MetadataDTO[];
    styles: MetadataDTO[];
    colorPalette: ColorPalette[];
    isPublicDomain: boolean;
    gallery: string[],
    thumbnail: string;
    location: ArtworkLocation;
    isContemporary: boolean;
    price: number;
    priceDigital: number;
    isForSale: boolean;
    isForSaleDigital: boolean;
    status: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
}

export interface ArtworkStep {
    id: number;
    title: string;
    step: number;
    key: string;
    component: Component | string;
}

// We omit the DB-specific fields that the client shouldn't manage manually
export interface NewArtwork extends Omit<
    Artwork,
    "_id" | "userId" | "createdAt" | "updatedAt" | "__v"
> { gallery: string[]; }

export interface EditedArtwork extends Omit<
    Artwork,
    "userId" | "createdAt" | "updatedAt" | "__v"
> {
    // We explicitly redefine userId as a string to match the "cleaned" ID
    // and keep _id included so the modal knows which document it's editing.
    userId: string;
}

export interface UserArtwork extends Omit<Artwork, "userId"> {
    userId: string; // Plain string ID as seen in your example
    galleryPriority: number; // Added because it appears in your response
}

export const artTypes: ArtTypeItem[] = [
    {
        id: 1,
        key: "painting",
        is3D: false,
        icon: 'ph:palette',
        label: { hy: "Գեղանկարչություն", en: "Painting", ru: "Живопись" },
    },
    {
        id: 2,
        key: "drawing",
        is3D: false,
        icon: 'ph:pen-nib',
        label: { hy: "Գծանկար", en: "Drawing", ru: "Рисунок" },
    },
    {
        id: 3,
        key: "sculpture",
        is3D: true,
        icon: 'ph:person',
        label: { hy: "Քանդակագործություն", en: "Sculpture", ru: "Скульптура" },
    },
    {
        id: 4,
        key: "handmade",
        is3D: true,
        icon: 'ph:hand-pointing',
        label: { hy: "Ձեռագործ", en: "Handmade", ru: "Ручная работа" },
    },
    // {
    //     id: 5,
    //     key: "book",
    //     is3D: true,
    //     icon: 'ph:book',
    //     label: { hy: "Գիրք", en: "Book", ru: "Книга" },
    // },
    {
        id: 6,
        key: "printmaking",
        is3D: false,
        icon: 'ph:cards',
        label: { hy: "Էստամպ", en: "Printmaking", ru: "Эстамп" },
    },
    {
        id: 7,
        key: "mosaic",
        is3D: false,
        icon: 'ph:grid-four',
        label: { hy: "Մոզաիկա", en: "Mosaic", ru: "Мозаика" },
    },
    {
        id: 8,
        key: "multimedia",
        is3D: true,
        icon: 'ph:cube',
        label: { hy: "Մուլտիմեդիա", en: "Multimedia", ru: "Мультимедиа" },
    },
];
